import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { postData, putData } from "../../../utility/Utility";

function AddCommissionRule() {
  const navigate = useNavigate();
  const location = useLocation();
  const editingRule = location.state?.ruleData || null;

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  const [category, setCategory] = useState(
    editingRule?.category?._id || editingRule?.category || ""
  );
  const [subcategory, setSubCategory] = useState(
    editingRule?.subcategory?._id || editingRule?.subcategory || ""
  );
  const [policyType, setPolicyType] = useState(editingRule?.policyType || "");
  const [slabRules, setSlabRules] = useState(
    editingRule?.slabRules?.length
      ? editingRule.slabRules.map((slab) => ({
          minAmount: slab.minAmount,
          maxAmount: slab.maxAmount,
          rate: slab.rate,
        }))
      : [{ minAmount: "", maxAmount: "", rate: "" }]
  );

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await postData("api/categories/list", {
          page: 1,
          limit: 100,
        });
        if (response?.status) {
          setCategories(response.data || []);
        } else {
          toast.error(response?.message || "Failed to fetch categories");
        }
      } catch (error) {
        console.error("Category fetch error:", error);
        toast.error("Error fetching categories");
      }
    };
    fetchCategories();
  }, []);

  // Fetch subcategories when category changes
  useEffect(() => {
    if (!category) return;
    const fetchSubCategories = async () => {
      try {
        const response = await postData("api/subcategory/list", {
          page: 1,
          limit: 100,
          category,
        });
        if (response?.status) {
          setSubCategories(response.data || []);
        } else {
          toast.error(response?.message || "Failed to fetch subcategories");
        }
      } catch (error) {
        console.error("Subcategory fetch error:", error);
        toast.error("Error fetching subcategories");
      }
    };
    fetchSubCategories();
  }, [category]);

  // Handle slab rule change
  const handleSlabChange = (index, field, value) => {
    const updated = [...slabRules];
    const numericValue = value === "" ? "" : Number(value);

    updated[index][field] = numericValue;

    // Show warning if max < min
    if (field === "maxAmount") {
      if (
        updated[index].minAmount !== "" &&
        numericValue < updated[index].minAmount
      ) {
        toast.warning("⚠️ Max amount should not be less than Min amount");
      }
      if (index < updated.length - 1 && numericValue !== "") {
        updated[index + 1].minAmount = numericValue + 1;
      }
    }

    if (field === "minAmount") {
      const currentMax = updated[index].maxAmount;
      if (currentMax !== "" && currentMax < numericValue) {
        toast.warning("⚠️ Max amount should not be less than Min amount");
      }
    }

    setSlabRules(updated);
  };

  const addSlab = () => {
    if (slabRules.length > 0) {
      const lastSlab = slabRules[slabRules.length - 1];
      const nextMin = lastSlab.maxAmount !== "" ? lastSlab.maxAmount + 1 : 0;
      setSlabRules([
        ...slabRules,
        { minAmount: nextMin, maxAmount: "", rate: "" },
      ]);
    } else {
      setSlabRules([{ minAmount: 0, maxAmount: "", rate: "" }]);
    }
  };

  const removeSlab = (index) => {
    setSlabRules(slabRules.filter((_, i) => i !== index));
  };

  // Submit form (Create or Update)
  const handleSubmit = async (e) => {
    e.preventDefault();

    const formattedSlabs = slabRules.map((slab) => ({
      minAmount: Number(slab.minAmount),
      maxAmount: Number(slab.maxAmount),
      rate: Number(slab.rate),
    }));

    const payload = {
      policyType,
      slabRules: formattedSlabs,
      category,
      subcategory: subcategory || null,
    };

    try {
      let response;
      if (editingRule) {
        payload.id = editingRule._id;
        response = await putData("api/commission-rules/update", payload);
      } else {
        response = await postData("api/commission-rules/create", payload);
      }

      if (response?.success) {
        toast.success(
          response?.message ||
            (editingRule ? "Updated successfully!" : "Created successfully!")
        );
        navigate("/commission-rule-list");
      } else {
        toast.error(response?.message || "Failed to save commission rule");
      }
    } catch (error) {
      console.error("Error saving commission rule:", error);
      toast.error("Error saving commission rule");
    }
  };

  return (
    <div className="content-page">
      <ToastContainer />
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          {editingRule ? "Edit Commission Rule" : "Create Commission Rule"}
        </h4>
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate("/commission-rules-list")}
        >
          <i className="fa fa-arrow-left me-2"></i> Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="card p-4 shadow-sm">
        {/* Category */}
        <div className="mb-3">
          <label className="form-label fw-semibold">Category</label>
          <select
            className="form-select"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setSubCategory("");
            }}
            required
          >
            <option value="">Select Category</option>
            {categories.map((cat) => (
              <option key={cat._id} value={cat._id}>
                {cat.title}
              </option>
            ))}
          </select>
        </div>

        {/* SubCategory */}
        <div className="mb-3">
          <label className="form-label fw-semibold">
            SubCategory (Optional)
          </label>
          <select
            className="form-select"
            value={subcategory}
            onChange={(e) => setSubCategory(e.target.value)}
            disabled={!category}
          >
            <option value="">Select SubCategory</option>
            {subCategories.map((sub) => (
              <option key={sub._id} value={sub._id}>
                {sub.title}
              </option>
            ))}
          </select>
        </div>

        {/* Policy Type */}
        <div className="mb-3">
          <label className="form-label fw-semibold">Policy Type</label>
          <select
            className="form-select"
            value={policyType}
            onChange={(e) => setPolicyType(e.target.value)}
            required
          >
            <option value="">Select Policy Type</option>
            <option value="fresh">Fresh</option>
            <option value="renewal">Renewal</option>
            <option value="port">Port</option>
          </select>
        </div>

        {/* Slab Rules */}
        {/* <h5 className="mt-10">Slab Rules</h5>
        {slabRules.map((slab, index) => (
          <div
            key={index}
            className="row g-3 align-items-end mb-3 border p-3  pt-4 rounded bg-light mt-8 slabmargin"
          >
            <div className="col-md-3 mt-4">
              <label className="form-label">Min Amount</label>
              <input
                type="number"
                className="form-control"
                value={slab.minAmount}
                onChange={(e) =>
                  handleSlabChange(index, "minAmount", e.target.value)
                }
                required
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Max Amount</label>
              <input
                type="number"
                className="form-control"
                value={slab.maxAmount}
                onChange={(e) =>
                  handleSlabChange(index, "maxAmount", e.target.value)
                }
                required
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Rate (%)</label>
              <input
                type="number"
                className="form-control"
                value={slab.rate}
                onChange={(e) =>
                  handleSlabChange(index, "rate", e.target.value)
                }
                required
              />
            </div>
            <div className="col-md-2 text-end">
              {slabRules.length > 1 && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => removeSlab(index)}
                >
                  <i className="fa fa-trash me-1"></i> Remove
                </button>
              )}
            </div>
          </div>
        ))} */}

        <div className="mb-3">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={addSlab}
          >
            <i className="fa fa-plus me-2"></i> Add Slab
          </button>
        </div>

        {/* Submit */}
        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => navigate("/commission-rule-list")}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            {editingRule ? "Update Rule" : "Save Rule"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddCommissionRule;
